<template>
  <div class="p-6">
    <!-- 页面标题 -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">辞书内容管理</h1>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">管理辞书的单词和句子内容</p>
    </div>

    <!-- 筛选器和操作按钮 -->
    <div class="mb-4 flex gap-4">
      <div class="flex-1">
        <input v-model="searchKeyword" type="text" placeholder="搜索单词..."
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
      </div>
      <div class="flex items-center gap-2">
        <button @click="loadContents"
          class="cancel-btn">
          刷新
        </button>
        <button @click="handleCreate"
          class="primary-btn gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          新增内容
        </button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- 内容列表 -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 overflow-hidden">
      <ContentTable :contents="contents" :selectable="false" @edit="handleEdit" @delete="handleDelete" />

      <!-- 空状态 -->
      <div v-if="contents.length === 0" class="text-center py-12">
        <p class="text-gray-500 dark:text-gray-400">暂无内容数据</p>
      </div>

      <!-- 分页 -->
      <Pagination v-if="total > 0" :total="total" v-model:current-page="currentPage" v-model:page-size="pageSize"
        @update:current-page="loadContents" @update:page-size="loadContents" />
    </div>

    <!-- 创建/编辑内容弹窗 -->
    <Modal :show="showContentModal" @close="closeContentModal">
      <template #header>
        {{ editingContent ? '编辑内容' : '新增内容' }}
      </template>
      <template #body>
        <ContentForm :initial-data="contentFormData" :loading="contentSubmitting" @submit="handleContentSubmit"
          @cancel="closeContentModal" />
      </template>
    </Modal>

    <!-- 删除确认对话框 -->
    <ConfirmDialog :show="showDeleteConfirm" type="danger" title="确认删除"
      :message="`确定要删除内容「${deletingContent?.text}」吗？此操作不可恢复。`" confirm-text="删除" cancel-text="取消"
      @confirm="confirmDelete" @cancel="cancelDelete" />
  </div>
</template>




<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue';
import ContentTable from '@/components/dictation/ContentTable.vue';
import ContentForm from '@/components/dictation/ContentForm.vue';
import Modal from '@/components/ui/Modal.vue';
import Pagination from '@/components/ui/Pagination.vue';
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue';
import { getContentListAPI, createContentAPI, updateContentAPI, deleteContentAPI } from '@/api/dictation/content';
import type { ContentSchema } from '@/api/dictation/content';
import { useToast } from '@/composables/useToast';
import { useDebounce } from '@/composables/useDebounce';

/**
 * 响应式数据
 */
const loading = ref(false);
const contents = ref<ContentSchema[]>([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(20);
const searchKeyword = ref('');

// 弹窗状态
const showContentModal = ref(false);
const contentSubmitting = ref(false);
const editingContent = ref<ContentSchema | null>(null);
const showDeleteConfirm = ref(false);
const deletingContent = ref<ContentSchema | null>(null);

// Toast 通知
const toast = useToast();

// 防抖搜索
const debouncedSearch = useDebounce(searchKeyword, 500);

// 表单数据
const contentFormData = computed(() => {
  if (!editingContent.value) return {};
  return {
    text: editingContent.value.text,
    content_type: editingContent.value.contentType,
    language_id: editingContent.value.languageId,
    ipa_uk: editingContent.value.ipaUk || '',
    ipa_us: editingContent.value.ipaUs || '',
    translation: editingContent.value.translation || '',
    hyphenation: editingContent.value.hyphenation || '',
  };
});

/**
 * 加载内容列表
 */
const loadContents = async () => {
  loading.value = true;
  try {
    const response = await getContentListAPI({
      page: currentPage.value,
      limit: pageSize.value,
      search: debouncedSearch.value || undefined,
    });
    contents.value = response.data?.contents || [];
    total.value = response.data?.total || 0;
  } catch (error) {
    console.error('加载内容列表失败:', error);
    toast.error('加载内容列表失败，请稍后重试');
  } finally {
    loading.value = false;
  }
};

/**
 * 新增内容
 */
const handleCreate = () => {
  editingContent.value = null;
  showContentModal.value = true;
};

/**
 * 编辑内容
 */
const handleEdit = (content: ContentSchema) => {
  editingContent.value = content;
  showContentModal.value = true;
};

/**
 * 关闭弹窗
 */
const closeContentModal = () => {
  showContentModal.value = false;
  editingContent.value = null;
};

/**
 * 提交表单
 */
const handleContentSubmit = async (data: any) => {
  contentSubmitting.value = true;
  try {
    if (editingContent.value) {
      await updateContentAPI(editingContent.value.id, data);
      toast.success('更新成功');
    } else {
      await createContentAPI(data);
      toast.success('创建成功');
    }
    closeContentModal();
    loadContents();
  } catch (error) {
    console.error('操作失败:', error);
    toast.error('操作失败，请稍后重试');
  } finally {
    contentSubmitting.value = false;
  }
};

/**
 * 删除内容
 */
const handleDelete = (content: ContentSchema) => {
  deletingContent.value = content;
  showDeleteConfirm.value = true;
};

/**
 * 确认删除
 */
const confirmDelete = async () => {
  if (!deletingContent.value) return;

  try {
    await deleteContentAPI(deletingContent.value.id);
    toast.success('删除成功');
    showDeleteConfirm.value = false;
    deletingContent.value = null;
    loadContents();
  } catch (error) {
    console.error('删除内容失败:', error);
    toast.error('删除失败，请稍后重试');
  }
};

/**
 * 取消删除
 */
const cancelDelete = () => {
  showDeleteConfirm.value = false;
  deletingContent.value = null;
};

// 监听防抖搜索变化
watch(debouncedSearch, () => {
  currentPage.value = 1;
  loadContents();
});

/**
 * 组件挂载时加载数据
 */
onMounted(() => {
  loadContents();
});
</script>
